import React, { useState, useEffect } from "react";
import axios from "../../AxiosInstance";
import { Tab, Tabs, Table, Button, Badge, Spinner, Modal } from "react-bootstrap";
import Sidebar from "../../components/Sidebar";
import toast from "react-hot-toast";
import { useLocation, useNavigate } from "react-router-dom";
import { BASE_URL } from "../../config/BaseUrls";

const AdminBookClubsManager = () => {
  const location = useLocation();
  const navigate = useNavigate();

  // Check if we're on an individual page (should hide tabs)
  const isIndividualPage = location.pathname.includes("club-management") ||
                          location.pathname.includes("book-duos") || 
                          location.pathname.includes("silent-readings");

  const [activeTab, setActiveTab] = useState(() => {
    if (location.pathname.includes("book-duos")) return "bookDuo";
    if (location.pathname.includes("silent-readings")) return "silentReading";
    return "bookClub";
  });
  const [bookClubs, setBookClubs] = useState([]);
  const [bookDuos, setBookDuos] = useState([]);
  const [silentReadings, setSilentReadings] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showAttendeesModal, setShowAttendeesModal] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [eventAttendees, setEventAttendees] = useState([]);
  const [removingAttendee, setRemovingAttendee] = useState(null);

  // Update active tab when route changes
  useEffect(() => {
    if (location.pathname.includes("book-duos")) {
      setActiveTab("bookDuo");
    } else if (location.pathname.includes("silent-readings")) {
      setActiveTab("silentReading");
    } else {
      setActiveTab("bookClub");
    }
  }, [location.pathname]);

  const fetchBookEvents = async (type) => {
    try {
      const response = await axios.get(`api/v1/books?type=${type}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      return response.data.data;
    } catch (error) {
      console.error(
        `Error fetching ${type}:`,
        error.response?.data?.message || error.message
      );
      throw error;
    }
  };

  const fetchAllEvents = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const [clubs, duos, readings] = await Promise.all([
        fetchBookEvents("Book Club"),
        fetchBookEvents("Book Duo"),
        fetchBookEvents("Silent Reading"),
      ]);
      
      // Sort by createdAt (newest first) - descending order
      const sortByNewest = (arr) => {
        return [...arr].sort((a, b) => {
          const dateA = new Date(a.createdAt || a._id);
          const dateB = new Date(b.createdAt || b._id);
          return dateB - dateA; // Descending order (newest first)
        });
      };
      
      setBookClubs(sortByNewest(clubs));
      setBookDuos(sortByNewest(duos));
      setSilentReadings(sortByNewest(readings));
    } catch (err) {
      setError(err.message);
      toast.error("Failed to fetch events");
    } finally {
      setIsLoading(false);
    }
  };

  const getEventDetails = (event) => ({
    title: event?.title || event?.bookDetails?.title || "N/A",
    author: event?.author || event?.bookDetails?.author || "N/A",
    date: event?.dateInfo
      ? `${event.dateInfo.day}/${event.dateInfo.month}/${event.dateInfo.year}`
      : event?.date
      ? new Date(event.date).toLocaleDateString("en-GB")
      : "N/A",
    time: event?.timeTrack
      ? `${event.timeTrack.minutes}:${event.timeTrack.seconds
          ?.toString()
          .padStart(2, "0")}`
      : event?.time || "N/A",
    attendees: event?.attendees?.length || 0,
    type: event?.type || event?.name,
  });

  const getUserBadges = (users) => (
    <div className="d-flex flex-wrap gap-1">
      {users && users.length > 0 ? (
        users.map((user, index) => (
          <Badge key={user._id || index} bg="info" className="me-1">
            {user.name || user.email || "Unknown"}
          </Badge>
        ))
      ) : (
        <span className="text-muted">No attendees yet</span>
      )}
    </div>
  );

  useEffect(() => {
    fetchAllEvents();
  }, []);

  const handleDeleteBook = async (bookId) => {
    if (!bookId) {
      console.error("Book ID is missing");
      toast.error("Book ID is missing. Cannot delete event.");
      return;
    }

    if (window.confirm("Are you sure you want to cancel this event?")) {
      try {
        console.log("Deleting book with ID:", bookId); // Debug log
        const response = await axios.delete(`api/v1/books/${bookId}`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        });
        
        if (response.data && response.data.success) {
          toast.success(response.data.message || "Event cancelled successfully");
        } else {
        toast.success("Event cancelled successfully");
        }
        fetchAllEvents(); // Refresh the table after delete
      } catch (error) {
        console.error("Error deleting event:", error);
        const errorMessage = error.response?.data?.message || error.message || "Failed to cancel the event";
        toast.error(errorMessage);
      }
    }
  };

  const handleViewAttendees = async (event) => {
    try {
      setSelectedEvent(event);
      // Fetch full attendee details with profile images
      const response = await axios.get(`api/v1/books/${event._id}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      const attendees = response.data.data?.attendees || [];
      setEventAttendees(attendees);
      setShowAttendeesModal(true);
    } catch (error) {
      console.error("Error fetching attendees:", error);
      toast.error("Failed to load attendees");
    }
  };

  const handleRemoveAttendee = async (attendeeId, attendeeName) => {
    if (!window.confirm(`Are you sure you want to remove ${attendeeName} from this event?`)) {
      return;
    }

    setRemovingAttendee(attendeeId);
    try {
      await axios.delete(
        `api/v1/books/remove-attendee/${selectedEvent._id}/${attendeeId}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      toast.success(`${attendeeName} removed successfully`);
      
      // Update the attendees list
      setEventAttendees(eventAttendees.filter((a) => a._id !== attendeeId));
      
      // Refresh the main table
      fetchAllEvents();
    } catch (error) {
      console.error("Error removing attendee:", error);
      toast.error(error.response?.data?.message || "Failed to remove attendee");
    } finally {
      setRemovingAttendee(null);
    }
  };

  return (
    <div className="d-md-flex gap-2">
      <Sidebar />
      <div className="containerr mt-4" style={{ flex: 1 }}>
        <h2 className="mb-4">Book Events Management</h2>
        {error && (
          <div className="alert alert-danger" role="alert">
            {error}
          </div>
        )}

        {isIndividualPage ? (
          // Show only the relevant table for individual pages
          activeTab === "bookClub" ? (
            <div className="table-responsive">
              <Table striped bordered hover responsive>
                <thead>
                  <tr>
                    <th style={{ minWidth: "150px" }}>Title</th>
                    <th>Author</th>
                    <th>Date</th>
                    <th>Time</th>
                    <th className="text-center">Attendees</th>
                    <th>Participants</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {isLoading ? (
                    <tr>
                      <td colSpan="7" className="text-center py-5">
                        <Spinner animation="border" variant="primary" />
                        <p className="mt-2">Loading events...</p>
                      </td>
                    </tr>
                  ) : bookClubs.length > 0 ? (
                    bookClubs.map((book) => {
                      const details = getEventDetails(book);
                      return (
                        <tr key={book._id}>
                          <td>{details.title}</td>
                          <td>{details.author}</td>
                          <td>{details.date}</td>
                          <td>{details.time}</td>
                          <td className="text-center">
                            <Badge
                              bg={
                                book.attendees?.length > 0
                                  ? "success"
                                  : "secondary"
                              }
                            >
                              {details.attendees}
                            </Badge>
                          </td>
                          <td>{getUserBadges(book.attendees)}</td>
                          <td>
                            <div className="d-flex gap-2">
                              <Button
                                variant="info"
                                size="sm"
                                onClick={() => handleViewAttendees(book)}
                                disabled={!book.attendees || book.attendees.length === 0}
                              >
                                View Attendees
                              </Button>
                            <Button
                              variant="danger"
                              size="sm"
                              onClick={() => handleDeleteBook(book._id)}
                            >
                              Cancel
                            </Button>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan="7" className="text-center text-muted py-4">
                        No book clubs found
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>
          ) : activeTab === "bookDuo" ? (
            <div className="table-responsive">
              <Table striped bordered hover responsive>
                <thead>
                  <tr>
                    <th style={{ minWidth: "150px" }}>Title</th>
                    <th>Author</th>
                    <th>Date</th>
                    <th>Time</th>
                    <th className="text-center">Attendees</th>
                    <th>Participants</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {isLoading ? (
                    <tr>
                      <td colSpan="7" className="text-center py-5">
                        <Spinner animation="border" variant="primary" />
                        <p className="mt-2">Loading events...</p>
                      </td>
                    </tr>
                  ) : bookDuos.length > 0 ? (
                    bookDuos.map((book) => {
                      const details = getEventDetails(book);
                      return (
                        <tr key={book._id}>
                          <td>{details.title}</td>
                          <td>{details.author}</td>
                          <td>{details.date}</td>
                          <td>{details.time}</td>
                          <td className="text-center">
                            <Badge
                              bg={
                                book.attendees?.length > 0
                                  ? "success"
                                  : "secondary"
                              }
                            >
                              {details.attendees}
                            </Badge>
                          </td>
                          <td>{getUserBadges(book.attendees)}</td>
                          <td>
                            <div className="d-flex gap-2">
                              <Button
                                variant="info"
                                size="sm"
                                onClick={() => handleViewAttendees(book)}
                                disabled={!book.attendees || book.attendees.length === 0}
                              >
                                View Attendees
                              </Button>
                            <Button
                              variant="danger"
                              size="sm"
                              onClick={() => handleDeleteBook(book._id)}
                            >
                              Cancel
                            </Button>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan="7" className="text-center text-muted py-4">
                        No book duos found
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>
          ) : (
            <div className="table-responsive">
              <Table striped bordered hover responsive>
                <thead>
                  <tr>
                    <th style={{ minWidth: "150px" }}>Title</th>
                    <th>Author</th>
                    <th>Date</th>
                    <th>Time</th>
                    <th className="text-center">Attendees</th>
                    <th>Participants</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {isLoading ? (
                    <tr>
                      <td colSpan="7" className="text-center py-5">
                        <Spinner animation="border" variant="primary" />
                        <p className="mt-2">Loading events...</p>
                      </td>
                    </tr>
                  ) : silentReadings.length > 0 ? (
                    silentReadings.map((book) => {
                      const details = getEventDetails(book);
                      return (
                        <tr key={book._id}>
                          <td>{details.title}</td>
                          <td>{details.author}</td>
                          <td>{details.date}</td>
                          <td>{details.time}</td>
                          <td className="text-center">
                            <Badge
                              bg={
                                book.attendees?.length > 0
                                  ? "success"
                                  : "secondary"
                              }
                            >
                              {details.attendees}
                            </Badge>
                          </td>
                          <td>{getUserBadges(book.attendees)}</td>
                          <td>
                            <Button
                              variant="danger"
                              size="sm"
                              onClick={() => handleDeleteBook(book._id)}
                            >
                              Cancel
                            </Button>
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan="7" className="text-center text-muted py-4">
                        No silent readings found
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>
          )
        ) : (
          // Show tabs for main pages (like /admin/events)
          <Tabs
            activeKey={activeTab}
            onSelect={(k) => {
              setActiveTab(k);
              // Only navigate if we're on /admin/events, otherwise just switch tabs
              // Don't navigate on /admin/events - just switch tabs
            }}
            className="mb-3"
          >
          <Tab eventKey="bookClub" title="Book Clubs">
            {isLoading ? (
              <div className="text-center py-5">
                <Spinner animation="border" variant="primary" />
                <p className="mt-2">Loading events...</p>
              </div>
            ) : (
              <div className="table-responsive">
                <Table striped bordered hover responsive>
                  <thead>
                    <tr>
                      <th style={{ minWidth: "150px" }}>Title</th>
                      <th>Author</th>
                      <th>Date</th>
                      <th>Time</th>
                      <th className="text-center">Attendees</th>
                      <th>Participants</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bookClubs.length > 0 ? (
                      bookClubs.map((book) => {
                        const details = getEventDetails(book);
                        return (
                          <tr key={book._id}>
                            <td>{details.title}</td>
                            <td>{details.author}</td>
                            <td>{details.date}</td>
                            <td>{details.time}</td>
                            <td className="text-center">
                              <Badge
                                bg={
                                  book.attendees?.length > 0
                                    ? "success"
                                    : "secondary"
                                }
                              >
                                {details.attendees}
                              </Badge>
                            </td>
                            <td>{getUserBadges(book.attendees)}</td>
                            <td>
                              <div className="d-flex gap-2">
                                <Button
                                  variant="info"
                                  size="sm"
                                  onClick={() => handleViewAttendees(book)}
                                  disabled={!book.attendees || book.attendees.length === 0}
                                >
                                  View Attendees
                                </Button>
                              <Button
                                variant="danger"
                                size="sm"
                                onClick={() => handleDeleteBook(book._id)}
                              >
                                Cancel
                              </Button>
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan="7" className="text-center text-muted py-4">
                          No book clubs found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>
            )}
          </Tab>

          <Tab eventKey="bookDuo" title="Book Duos">
            {isLoading ? (
              <div className="text-center py-5">
                <Spinner animation="border" variant="primary" />
                <p className="mt-2">Loading events...</p>
              </div>
            ) : (
              <div className="table-responsive">
                <Table striped bordered hover responsive>
                  <thead>
                    <tr>
                      <th style={{ minWidth: "150px" }}>Title</th>
                      <th>Author</th>
                      <th>Date</th>
                      <th>Time</th>
                      <th className="text-center">Attendees</th>
                      <th>Participants</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bookDuos.length > 0 ? (
                      bookDuos.map((book) => {
                        const details = getEventDetails(book);
                        return (
                          <tr key={book._id}>
                            <td>{details.title}</td>
                            <td>{details.author}</td>
                            <td>{details.date}</td>
                            <td>{details.time}</td>
                            <td className="text-center">
                              <Badge
                                bg={
                                  book.attendees?.length > 0
                                    ? "success"
                                    : "secondary"
                                }
                              >
                                {details.attendees}
                              </Badge>
                            </td>
                            <td>{getUserBadges(book.attendees)}</td>
                            <td>
                              <div className="d-flex gap-2">
                                <Button
                                  variant="info"
                                  size="sm"
                                  onClick={() => handleViewAttendees(book)}
                                  disabled={!book.attendees || book.attendees.length === 0}
                                >
                                  View Attendees
                                </Button>
                              <Button
                                variant="danger"
                                size="sm"
                                onClick={() => handleDeleteBook(book._id)}
                              >
                                Cancel
                              </Button>
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan="7" className="text-center text-muted py-4">
                          No book duos found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>
            )}
          </Tab>

          <Tab eventKey="silentReading" title="Silent Readings">
            {isLoading ? (
              <div className="text-center py-5">
                <Spinner animation="border" variant="primary" />
                <p className="mt-2">Loading events...</p>
              </div>
            ) : (
              <div className="table-responsive">
                <Table striped bordered hover responsive>
                  <thead>
                    <tr>
                      <th style={{ minWidth: "150px" }}>Title</th>
                      <th>Author</th>
                      <th>Date</th>
                      <th>Time</th>
                      <th className="text-center">Attendees</th>
                      <th>Participants</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {silentReadings.length > 0 ? (
                      silentReadings.map((book) => {
                        const details = getEventDetails(book);
                        return (
                          <tr key={book._id}>
                            <td>{details.title}</td>
                            <td>{details.author}</td>
                            <td>{details.date}</td>
                            <td>{details.time}</td>
                            <td className="text-center">
                              <Badge
                                bg={
                                  book.attendees?.length > 0
                                    ? "success"
                                    : "secondary"
                                }
                              >
                                {details.attendees}
                              </Badge>
                            </td>
                            <td>{getUserBadges(book.attendees)}</td>
                            <td>
                              <Button
                                variant="danger"
                                size="sm"
                                onClick={() => handleDeleteBook(book._id)}
                              >
                                Cancel
                              </Button>
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan="7" className="text-center text-muted py-4">
                          No silent readings found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>
            )}
          </Tab>
        </Tabs>
        )}

        <div className="d-flex justify-content-between align-items-center mt-3 mb-5">
          <Button
            variant="primary"
            onClick={fetchAllEvents}
            disabled={isLoading}
            style={{ backgroundColor: "#34c3c9", borderColor: "#34c3c9" }}
          >
            {isLoading ? "Refreshing..." : "Refresh Data"}
          </Button>
          <div className="text-muted">
            <strong>Total Events:</strong>{" "}
            {bookClubs.length + bookDuos.length + silentReadings.length}
          </div>
        </div>
      </div>

      {/* Attendees Modal */}
      <Modal
        show={showAttendeesModal}
        onHide={() => {
          setShowAttendeesModal(false);
          setSelectedEvent(null);
          setEventAttendees([]);
        }}
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            Attendees - {selectedEvent?.title || "Event"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {eventAttendees.length === 0 ? (
            <p className="text-muted text-center py-4">No attendees yet</p>
          ) : (
            <div className="table-responsive">
              <Table striped bordered hover>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th className="text-center">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {eventAttendees.map((attendee) => (
                    <tr key={attendee._id}>
                      <td>
                        <div className="d-flex align-items-center gap-2">
                          {attendee.profileImage ? (
                            <img
                              src={
                                attendee.profileImage.startsWith("http")
                                  ? attendee.profileImage
                                  : `${BASE_URL}/uploads/${attendee.profileImage}`
                              }
                              alt={attendee.name}
                              style={{
                                width: "40px",
                                height: "40px",
                                borderRadius: "50%",
                                objectFit: "cover",
                              }}
                              onError={(e) => {
                                e.target.style.display = "none";
                              }}
                            />
                          ) : (
                            <div
                              style={{
                                width: "40px",
                                height: "40px",
                                borderRadius: "50%",
                                backgroundColor: "#34c3c9",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                color: "white",
                                fontWeight: "bold",
                              }}
                            >
                              {(attendee.name || attendee.email || "U")[0].toUpperCase()}
                            </div>
                          )}
                          <span>{attendee.name || attendee.email || "Unknown"}</span>
                        </div>
                      </td>
                      <td>{attendee.email || "N/A"}</td>
                      <td className="text-center">
                        <Button
                          variant="danger"
                          size="sm"
                          onClick={() =>
                            handleRemoveAttendee(
                              attendee._id,
                              attendee.name || attendee.email || "this attendee"
                            )
                          }
                          disabled={removingAttendee === attendee._id}
                        >
                          {removingAttendee === attendee._id ? (
                            <>
                              <Spinner
                                animation="border"
                                size="sm"
                                className="me-2"
                              />
                              Removing...
                            </>
                          ) : (
                            "Remove"
                          )}
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => {
              setShowAttendeesModal(false);
              setSelectedEvent(null);
              setEventAttendees([]);
            }}
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default AdminBookClubsManager;
